<template>
  <v-dialog v-model="isOpen" max-width="480">
    <v-card data-cy="lead-status-modal">
      <v-card-title class="pa-4">
        {{ t("message.LeadStatus.title") }}
      </v-card-title>
      <v-card-text class="pa-4">
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.LeadStatus.schoolLabel") }}
          </div>
          <div class="text-body-1">{{ props.lead?.school_name || "-" }}</div>
        </div>
        <div class="mb-4">
          <div class="text-caption text-grey">
            {{ t("message.LeadStatus.currentStatus") }}
          </div>
          <div class="text-body-2" data-cy="lead-status-current">
            {{ currentStatusLabel }}
          </div>
        </div>
        <v-select
          v-model="selectedStatus"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          :label="t('message.LeadStatus.newStatus')"
          data-cy="lead-status-select"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item
              v-bind="itemProps"
              :data-cy="`lead-status-option-${item.value}`"
            />
          </template>
        </v-select>
        <div
          v-if="selectedStatus === 'converted'"
          class="text-body-2 text-grey mt-2"
          data-cy="lead-status-converted-note"
        >
          {{ t("message.LeadStatus.convertedNote") }}
        </div>
        <div
          v-else-if="selectedStatus === 'rejected' || selectedStatus === 'cancelled'"
          class="text-body-2 text-grey mt-2"
          data-cy="lead-status-suppressed-note"
        >
          {{ t("message.LeadStatus.suppressedNote") }}
        </div>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn
          color="error"
          variant="tonal"
          :loading="saving"
          :disabled="props.lead?.status === 'rejected'"
          @click="quickReject"
          data-cy="lead-status-quick-reject-btn"
        >
          {{ t("message.LeadStatus.reject") }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="outlined"
          @click="close"
          :disabled="saving"
          data-cy="lead-status-cancel-btn"
        >
          {{ t("message.AccountSettings.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="save"
          :loading="saving"
          :disabled="!canSave"
          data-cy="lead-status-save-btn"
        >
          {{ t("message.AccountSettings.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAlertsStore } from "@/stores/alerts";
import useLeads from "@/stores/support/leads";
import type { TrialMembership } from "@/stores/support/trials";

const props = defineProps<{
  modelValue: boolean;
  lead: TrialMembership | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

const { t } = useI18n();
const alertsStore = useAlertsStore();
const leadsStore = useLeads();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const statusValues: TrialMembership["status"][] = [
  "requested",
  "pending",
  "fullfilled",
  "expired",
  "cancelled",
  "rejected",
  "converted",
];

const statusOptions = computed(() => {
  return statusValues.map((value) => ({
    value,
    title: t(`message.LeadStatus.statuses.${value}`),
  }));
});

const selectedStatus = ref<TrialMembership["status"] | "">("");
const saving = ref(false);

const currentStatusLabel = computed(() => {
  if (!props.lead?.status) return "-";
  return t(`message.LeadStatus.statuses.${props.lead.status}`);
});

const canSave = computed(() => {
  return !!props.lead && !!selectedStatus.value && selectedStatus.value !== props.lead.status;
});

watch(
  () => props.lead,
  (lead) => {
    selectedStatus.value = lead?.status || "";
  },
  { immediate: true }
);

const close = () => {
  isOpen.value = false;
};

const applyStatus = async (status: TrialMembership["status"]) => {
  if (!props.lead) return;
  saving.value = true;
  try {
    const result = await leadsStore.updateLead(props.lead.trial_id, { status });
    if (result.status) {
      alertsStore.addAlert("success", t("message.LeadStatus.updatedSuccess"), 5);
      close();
    } else {
      alertsStore.addAlert("error", result.message || t("message.LeadStatus.updateError"), 10);
    }
  } catch (error) {
    console.error("Error updating lead status:", error);
    alertsStore.addAlert("error", t("message.LeadStatus.updateError"), 10);
  } finally {
    saving.value = false;
  }
};

const quickReject = () => applyStatus("rejected");

const save = () => {
  if (!selectedStatus.value) return;
  applyStatus(selectedStatus.value);
};
</script>

<style scoped lang="scss">
.text-grey {
  color: rgba(0, 0, 0, 0.6);
}
</style>
